import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff, CheckCircle2, Loader2, Lock, AlertCircle } from 'lucide-react';
import api from '../../lib/api';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);
    const redirectTimerRef = useRef(null);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) {
                clearTimeout(redirectTimerRef.current);
            }
        };
    }, []);

    const validatePassword = (pwd) => {
        const errs = [];
        if (pwd.length < 8) errs.push('Au moins 8 caractères');
        if (!/[A-Z]/.test(pwd)) errs.push('Au moins une majuscule');
        if (!/[a-z]/.test(pwd)) errs.push('Au moins une minuscule');
        if (!/[0-9]/.test(pwd)) errs.push('Au moins un chiffre');
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate
        const errors = validatePassword(password);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }
        
        if (password !== confirmPassword) {
            setErrorMessage('Les mots de passe ne correspondent pas');
            return;
        }
        
        setLoading(true);
        setErrorMessage('');
        
        try {
            await api.post('/auth/reset-password', {
                token,
                new_password: password
            });
            
            // Success - show success state and redirect
            setSuccess(true);
            
            // Use ref to manage timer and redirect via window.location
            redirectTimerRef.current = setTimeout(() => {
                window.location.replace('/login');
            }, 2500);
            
        } catch (error) {
            const detail = error.response?.data?.detail;
            setErrorMessage(detail || 'Erreur lors de la réinitialisation. Le lien a peut-être expiré.');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0a0a1a] p-4">
                <Card className="w-full max-w-md bg-slate-900 border-white/10">
                    <CardHeader className="text-center">
                        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
                        <CardTitle className="text-red-400">Lien invalide</CardTitle>
                        <CardDescription className="text-white/60">
                            Ce lien de réinitialisation est invalide ou a expiré.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Link to="/forgot-password">
                            <Button className="w-full">
                                Demander un nouveau lien
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0a0a1a] p-4">
                <Card className="w-full max-w-md bg-slate-900 border-white/10">
                    <CardHeader className="text-center">
                        <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-400" />
                        <CardTitle className="text-green-400">Mot de passe réinitialisé !</CardTitle>
                        <CardDescription className="text-white/60">
                            Votre mot de passe a été changé avec succès.
                            <br />
                            Redirection vers la page de connexion...
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <Link to="/login">
                            <Button variant="outline" className="mt-2">
                                Aller à la connexion maintenant
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0a0a1a] p-4">
            <Card className="w-full max-w-md bg-slate-900 border-white/10">
                <CardHeader className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#681793]/10 rounded-full flex items-center justify-center">
                        <Lock className="w-8 h-8 text-[#681793]" />
                    </div>
                    <CardTitle className="text-white">Nouveau mot de passe</CardTitle>
                    <CardDescription className="text-white/60">
                        Choisissez un mot de passe sécurisé pour votre compte.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Error message display */}
                    {errorMessage && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{errorMessage}</span>
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setValidationErrors(validatePassword(e.target.value));
                                        setErrorMessage('');
                                    }}
                                    placeholder="••••••••••"
                                    required
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            
                            {/* Password requirements */}
                            <div className="space-y-1 text-sm">
                                <p className={password.length >= 8 ? 'text-green-400' : 'text-white/40'}>
                                    {password.length >= 8 ? '✓' : '○'} Au moins 8 caractères
                                </p>
                                <p className={/[A-Z]/.test(password) ? 'text-green-400' : 'text-white/40'}>
                                    {/[A-Z]/.test(password) ? '✓' : '○'} Au moins une majuscule
                                </p>
                                <p className={/[a-z]/.test(password) ? 'text-green-400' : 'text-white/40'}>
                                    {/[a-z]/.test(password) ? '✓' : '○'} Au moins une minuscule
                                </p>
                                <p className={/[0-9]/.test(password) ? 'text-green-400' : 'text-white/40'}>
                                    {/[0-9]/.test(password) ? '✓' : '○'} Au moins un chiffre
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setErrorMessage('');
                                }}
                                placeholder="••••••••••"
                                required
                                data-testid="confirm-password-input"
                            />
                            {confirmPassword && password !== confirmPassword && (
                                <p className="text-sm text-red-500">
                                    Les mots de passe ne correspondent pas
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || validationErrors.length > 0 || password !== confirmPassword}
                            className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D]"
                            data-testid="reset-password-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Réinitialisation...
                                </>
                            ) : (
                                'Réinitialiser mon mot de passe'
                            )}
                        </Button>
                    </form>

                    <div className="mt-4 text-center">
                        <Link 
                            to="/login" 
                            className="text-sm text-muted-foreground hover:text-foreground"
                        >
                            Retour à la connexion
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
